import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, SearchX, ShieldCheck } from "lucide-react";
import Nav from "@/components/Nav";
import { PUBLIC_CERTIFICATE_VERIFY_API } from "@/user-panel/apis/apis";

const fmt = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

/** Public certificate check (QR code on the certificate opens this page). */
const VerifyCertificate = () => {
  const { certificateId } = useParams();
  const [search] = useSearchParams();
  const id = (certificateId || search.get("id") || "").trim().toUpperCase();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    if (!id) {
      setState({ loading: false, data: null, error: "No certificate ID was given." });
      return undefined;
    }
    PUBLIC_CERTIFICATE_VERIFY_API(id)
      .then((res) => alive && setState({ loading: false, data: res?.data || null, error: res?.data ? null : "No certificate found with this ID." }))
      .catch((err) => alive && setState({ loading: false, data: null, error: err?.response?.data?.message || "No certificate found with this ID." }));
    return () => {
      alive = false;
    };
  }, [id]);

  const c = state.data;
  const valid = c && c.status === "valid";

  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />
      <div className="relative min-h-screen flex items-center justify-center px-6 noise overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="verify-certificate-card"
          className="relative z-10 w-full max-w-lg rounded-2xl border border-[#3d414f] bg-[#0a0a12]/90 backdrop-blur-xl p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]"><ShieldCheck size={18} /></span>
            <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#16fcfb]">Certificate check</span>
          </div>
          {state.loading ? (
            <p className="text-white/60">Checking certificate {id}…</p>
          ) : c ? (
            <>
              <div className={`flex items-center gap-2 text-2xl font-display font-700 ${valid ? "text-[#4ade80]" : "text-[#f87171]"}`}>
                {valid ? <BadgeCheck /> : <SearchX />} {valid ? "Valid certificate" : `Certificate ${c.status || "not valid"}`}
              </div>
              <dl className="mt-6 grid grid-cols-3 gap-y-3 text-sm">
                <dt className="text-white/50">Certificate ID</dt><dd className="col-span-2 font-mono2">{c.certificate_id || id}</dd>
                <dt className="text-white/50">Name</dt><dd className="col-span-2 font-semibold">{c.holder_name || "—"}</dd>
                <dt className="text-white/50">Certification</dt><dd className="col-span-2">{c.exam_name || c.exam_code} ({c.exam_code})</dd>
                <dt className="text-white/50">Issued</dt><dd className="col-span-2">{fmt(c.issued_at)}</dd>
              </dl>
            </>
          ) : (
            <div className="flex items-center gap-2 text-xl font-display font-700 text-[#f87171]">
              <SearchX /> {state.error}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
